/**
 * @homero/pricing — the single source of truth for Homero pricing.
 *
 * Import money arithmetic, rate tables and the price engines from here. Nothing
 * downstream should hardcode a price, a tax rate or a discount percentage; if
 * you find yourself writing `8.875` or `* 20` in an app, it belongs in ./rates.
 */

export {
  ADD_ON_COST_CENTS,
  CLEANING_TYPE_IDS,
  CLEANING_TYPE_LABELS,
  CLEANING_TYPE_LEGACY_NAMES,
  FLEXIBLE_DISCOUNT_CENTS,
  FREQUENCY_DISCOUNT_PERCENT,
  FREQUENCY_INTERVAL_COUNT,
  RATE_CARDS,
  MAX_ROOMS,
  ROOM_COST_CENTS,
  flatRoomPrices,
  TAX_RATE,
  V1_FROM,
  V2_FROM,
  V3_FROM,
  V4_FROM,
  V5_FROM,
  getCleaningTypePriceCents,
  getFrequencyDiscountPercent,
  getFrequencyIntervalCount,
  getRateCard,
  isFlexible,
} from "./rates";
export type {
  CleaningTypeId,
  CleaningTypePrices,
  Frequency,
  RateCard,
  RoomPrices,
} from "./rates";

export {
  UnknownCleaningTypeError,
  includesFreeExtras,
  resolveCleaningType,
  resolveCleaningTypeOrThrow,
  toLabel,
  toLegacyName,
} from "./cleaning-types";

export {
  applyRate,
  assertCents,
  centsToDollars,
  dollarsToCents,
  formatCents,
  parseMoneyToCents,
  percentDiscountCents,
  percentOfCents,
  percentToBasisPoints,
  readMoneyColumnToCents,
  roundHalfAwayFromZero,
  sumCents,
  toStripeAmount,
} from "./money";
export type { Cents, Rate } from "./money";

export {
  DAYS_OF_WEEK,
  assertValidPricingConfig,
  copyTimeOfDayRules,
  noTimeOfDayRules,
  dayOfWeekForServiceDate,
  defaultPricingConfig,
  fromPublicPricingConfig,
  hasAnySurge,
  parsePricingConfig,
  perDay,
  pricingConfigFingerprint,
  pricingVariesByDayOfWeek,
  serializePricingConfig,
  toPublicPricingConfig,
  windowOffersDay,
  withProximityTier,
  withSameDayForDate,
  withoutDayOfWeekVariation,
  zeroSurgeConfig,
} from "./config";
export type {
  ArrivalWindow,
  DayOfWeek,
  PerDay,
  PricingConfig,
  ProximityConfig,
  ProximityTier,
  PublicPricingConfig,
  SameDayConfig,
  SerializedPricingConfig,
  TimeOfDayRule,
  TimeOfDayRules,
} from "./config";


export {
  flexibleDiscountCentsFor,
  isSameDayServiceDate,
  resolveSameDayAvailability,
} from "./same-day";
export type {
  SameDayAvailability,
  SameDayClock,
  SameDayUnavailableReason,
} from "./same-day";

export {
  getAddOnsCostCents,
  getBookingPrice,
  getRoomsCents,
  getTaskPrice,
  getTaxCents,
} from "./decimal";
export type {
  DecimalBookingInput,
  PriceBreakdown,
  TaskLineItem,
  TaskPriceBreakdown,
} from "./decimal";

export {
  getLegacyAddOnsCost,
  getLegacyAppointmentValue,
  getLegacyBreakdown,
  getLegacyTax,
  getLegacyTotalPrice,
} from "./legacy";
export type { LegacyBookingInput, LegacyBreakdown } from "./legacy";

export { quoteBooking, usesDecimalPricing } from "./quote";
export type { Quote, QuoteInput } from "./quote";

export {
  linesReconcile,
  receiptAmounts,
  toBookingLines,
  toPlainText,
  toTaskLines,
} from "./format";
export type { LineKind, MoneyLine } from "./format";

export {
  ARRIVAL_BLOCKS,
  ARRIVAL_SLOT_MINUTES,
  FLEXIBLE_WINDOW,
  blocksFor,
  normalizeZip,
  parseArrival,
} from "./arrival";
export type { Arrival, MinuteRange } from "./arrival";
